import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { useNavigation } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { useInferDevices } from "@/shared/domain/devices/hooks/useInferDevices";
import { type FileRecord } from "@/shared/domain/files";
import { useFileRecords } from "@/shared/domain/files/hooks";
import { type CreateSessionRequest } from "@/shared/domain/sessions";
import { useCreateSessionFromFiles } from "@/shared/domain/sessions/hooks";
import { userFacingErrorMessage } from "@/shared/http";

interface UseCreateSessionFormParams {
  orgId: string;
  datasetId: string | undefined;
  fileIds: string[] | undefined;
  directoryPatterns: string[] | undefined;
}

export interface CreateSessionForm {
  name: string;
  setName: (name: string) => void;

  selectedFiles: FileRecord[];
  isPrefilling: boolean;
  prefillError: Error | null;
  mergeFiles: (incoming: FileRecord[]) => void;
  removeFiles: (fileIds: string[]) => void;

  inferredDeviceIds: string[];
  isInferringDevices: boolean;
  inferDevicesError: Error | null;
  explicitDeviceIds: string[];
  addDevice: (deviceId: string) => void;
  removeDevice: (deviceId: string) => void;

  customFieldValues: Record<string, unknown>;
  setCustomFieldValues: (values: Record<string, unknown>) => void;

  submitError: React.ReactNode;
  isSubmitting: boolean;
  submit: () => Promise<void>;
}

/**
 * Owns the Create-session form: the editable name, the working file selection
 * (prefilled from the entry point), the inferred/explicit device sets, the
 * custom-field values, and the submit flow. The page component renders against
 * this state and holds no data logic of its own.
 */
export function useCreateSessionForm({
  orgId,
  datasetId,
  fileIds,
  directoryPatterns,
}: UseCreateSessionFormParams): CreateSessionForm {
  const { goto } = useNavigation();
  const { datasetService } = useDomainServices();

  const [name, setName] = React.useState("");
  const [selectedFiles, setSelectedFiles] = React.useState<FileRecord[]>([]);
  const [explicitDeviceIds, setExplicitDeviceIds] = React.useState<string[]>(
    [],
  );
  const [customFieldValues, setCustomFieldValues] = React.useState<
    Record<string, unknown>
  >({});
  const [submitError, setSubmitError] = React.useState<React.ReactNode>(null);

  // Resolves explicitly-selected files by id through the per-file query
  // cache; concurrent fetches for the same file are deduplicated.
  const { fetchFileRecords } = useFileRecords(fileIds ?? []);

  // Prefill the file selection. When the caller pre-selected files/directories
  // (file-browser bulk selection), prefill exactly those: explicit files are
  // fetched by id, and selected directories are expanded server-side.
  // Otherwise, when a dataset is in scope, prefill all of its files. The
  // top-level no-dataset entry prefills nothing — the user picks files via the
  // FilePicker.
  const prefillQuery = useQuery({
    queryKey: [
      "createSessionPrefillFiles",
      datasetId,
      fileIds,
      directoryPatterns,
    ],
    queryFn: async ({ signal }) => {
      const hasSelection =
        (fileIds?.length ?? 0) > 0 || (directoryPatterns?.length ?? 0) > 0;

      if (!hasSelection) {
        // Whole-dataset entry point: prefill every file in the dataset.
        // `enabled` guarantees datasetId is defined when there is no
        // selection, but guard rather than assert.
        return datasetId === undefined
          ? []
          : datasetService.listAllFiles(datasetId, { abortSignal: signal });
      }

      const [fromFiles, fromDirectories] = await Promise.all([
        fileIds && fileIds.length > 0 ? fetchFileRecords() : [],
        // Directory patterns are dataset-relative; without a dataset in
        // scope they are ignored.
        directoryPatterns &&
        directoryPatterns.length > 0 &&
        datasetId !== undefined
          ? datasetService.listAllFiles(datasetId, {
              includePatterns: directoryPatterns,
              abortSignal: signal,
            })
          : [],
      ]);

      // A file can be both explicitly selected and a member of a selected
      // directory.
      return dedupeByFileId([...fromFiles, ...fromDirectories]);
    },
    enabled: datasetId !== undefined || (fileIds?.length ?? 0) > 0,
  });

  // Latch the prefill per input set: a later refetch of the same inputs
  // (e.g. a stale-then-fresh cache serve) doesn't clobber files the user has
  // already added or removed, while changed inputs (a same-route
  // renavigation carrying a different seed) re-apply the new prefill.
  // Mid-prefill edits can't be clobbered either: FilesSection disables its
  // edit affordances while the prefill is in flight.
  const prefillInputsKey = JSON.stringify([
    datasetId,
    fileIds,
    directoryPatterns,
  ]);
  const prefilledForInputsRef = React.useRef<string | null>(null);
  React.useEffect(
    function prefillSelectionOncePerInputs() {
      if (
        prefilledForInputsRef.current === prefillInputsKey ||
        prefillQuery.data === undefined
      ) {
        return;
      }
      setSelectedFiles(prefillQuery.data);
      prefilledForInputsRef.current = prefillInputsKey;
    },
    [prefillInputsKey, prefillQuery.data],
  );

  const {
    deviceIds: inferredDeviceIds,
    isLoading: isInferringDevices,
    error: inferDevicesError,
  } = useInferDevices(
    selectedFiles.map((file) => file.file_id),
    orgId,
  );

  const createMutation = useCreateSessionFromFiles();

  const mergeFiles = (incoming: FileRecord[]) => {
    setSelectedFiles((prev) => dedupeByFileId([...prev, ...incoming]));
  };

  const removeFiles = (fileIdsToRemove: string[]) => {
    const toRemove = new Set(fileIdsToRemove);
    setSelectedFiles((prev) =>
      prev.filter((file) => !toRemove.has(file.file_id)),
    );
  };

  const addDevice = (deviceId: string) => {
    setExplicitDeviceIds((prev) =>
      prev.includes(deviceId) ? prev : [...prev, deviceId],
    );
  };

  const removeDevice = (deviceId: string) => {
    setExplicitDeviceIds((prev) => prev.filter((id) => id !== deviceId));
  };

  const submit = async () => {
    setSubmitError(null);
    const request: CreateSessionRequest = {};
    const trimmedName = name.trim();
    if (trimmedName) {
      request.name = trimmedName;
    }
    if (explicitDeviceIds.length > 0) {
      request.device_ids = explicitDeviceIds;
    }
    if (Object.keys(customFieldValues).length > 0) {
      request.custom_fields = customFieldValues;
    }

    try {
      const session = await createMutation.mutateAsync({
        request,
        files: selectedFiles.map((file) => ({ file_id: file.file_id })),
        resourceOwnerId: orgId,
      });
      goto.session({ sessionId: session.session_id });
    } catch (error) {
      // Every submit failure is swallowed and surfaced inline;
      // `userFacingErrorMessage` picks server copy vs. the fallback.
      setSubmitError(
        userFacingErrorMessage(
          error,
          "Failed to create session. Please retry.",
        ),
      );
    }
  };

  return {
    name,
    setName,
    selectedFiles,
    isPrefilling: prefillQuery.isLoading,
    prefillError: prefillQuery.error,
    mergeFiles,
    removeFiles,
    inferredDeviceIds,
    isInferringDevices,
    inferDevicesError,
    explicitDeviceIds,
    addDevice,
    removeDevice,
    customFieldValues,
    setCustomFieldValues,
    submitError,
    isSubmitting: createMutation.isPending,
    submit,
  };
}

/** Deduplicates by `file_id`; on a collision the later entry wins. */
function dedupeByFileId(files: FileRecord[]): FileRecord[] {
  const byId = new Map(files.map((file) => [file.file_id, file]));
  return Array.from(byId.values());
}
